import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Flame, ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/audit";

const KEY = "vs:streak:last_reset_at";
const TTL_MS = 1000 * 60 * 60 * 36; // 36h window

/** PR-33 — Streak Recovery surface.
 * Renders only after a streak_reset within the last 36h. Pure
 * frontend — listens to the existing `streak_reset` audit event via a
 * thin window bridge written in this file.
 */
export function StreakRecoveryCard() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(KEY);
      if (raw) {
        const ts = Number(raw);
        if (Number.isFinite(ts) && Date.now() - ts < TTL_MS) setVisible(true);
      }
    } catch { /* noop */ }
    const onReset = () => {
      try { window.localStorage.setItem(KEY, String(Date.now())); } catch { /* noop */ }
      setVisible(true);
    };
    window.addEventListener("vs:streak:reset", onReset as EventListener);
    return () => window.removeEventListener("vs:streak:reset", onReset as EventListener);
  }, []);

  useEffect(() => {
    if (visible) void trackEvent("streak_recovery_view", {});
  }, [visible]);

  if (!visible) return null;

  return (
    <section
      aria-label="Recupera tu racha"
      className="overflow-hidden rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500 text-background">
          <Flame className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-500">
            🔥 Recupera tu racha
          </div>
          <p className="text-[12px] leading-snug text-foreground">
            Completa una predicción hoy y vuelve a empezar tu racha.
          </p>
        </div>
        <Link
          to="/quinielas"
          onClick={() => {
            void trackEvent("streak_recovery_click", {});
            try { window.localStorage.removeItem(KEY); } catch { /* noop */ }
            setVisible(false);
          }}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-amber-500 px-3 py-1.5 text-[11px] font-black text-background"
        >
          Recuperar <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </section>
  );
}
